import { useMemo, useState } from "react";
import { App, Empty, List, Popover } from "antd5"; // sketchy looking popover with andt global classes, perhaps redesign with Phil or Saph, upgrade message to new context

import { TextLink } from "components/actions/Links";
import TextButton from "components/actions/TextButton";
import Tooltip from "components/ui/tooltip/Tooltip";
import { ClipboardContent, copyToClipboard } from "lib/clipboard";
import { Table } from "lib/core_components/Table";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import {
  ProcurementContactFromQuery,
  useBuyerContacts,
} from "lib/hooks/api/buyer/useBuyerContacts";
import { useSubscription } from "lib/hooks/subscription";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { EventNames, useTracking } from "lib/tracking";
import { BuyerDetails, DataTypeName } from "lib/types/models";
import { linkedinPeopleSearchURL } from "lib/utils";

import css from "./Procurement.module.scss";

type ContactValue = ProcurementContactFromQuery["emails"][number];

export function ProcurementContacts({ buyer }: { buyer: BuyerDetails }): JSX.Element {
  const { logEvent } = useTracking();

  const { data, isLoading, isError } = useBuyerContacts(buyer.guid);

  const [pageSize, setPageSize] = useTablePageSize("buyer-details-procurement-table");
  const [currentPage, setCurrentPage] = useState(1);

  // The query's own shape feeds the table directly — the only thing that varies per buyer is the
  // LinkedIn search URL.
  const columns: ColumnType<ProcurementContactFromQuery>[] = useMemo(
    () => [
      { key: "name", title: "Name", dataIndex: "name" },
      {
        key: "emails",
        title: "Email",
        render: (_, contact) => (
          <ContactValuesCell
            values={contact.emails}
            contactName={contact.name}
            dataType="email"
            label="Emails"
            placement="right"
          />
        ),
      },
      {
        key: "phoneNumbers",
        title: "Phone Number",
        render: (_, contact) => (
          <ContactValuesCell
            values={contact.phoneNumbers}
            contactName={contact.name}
            dataType="phone"
            label="Phone numbers"
            placement="left"
          />
        ),
      },
      {
        key: "linkedin",
        title: "LinkedIn",
        render: (_, contact) => (
          <TextLink
            eventName={EventNames.linkedinClicked}
            to={linkedinPeopleSearchURL(contact.name, buyer.name)}
            targetType="external"
          >
            &nbsp;Search on Linkedin
          </TextLink>
        ),
      },
      { key: "lastReferenced", title: "Last Active", dataIndex: "lastReferenced" },
    ],
    [buyer.name],
  );

  return (
    <div>
      <Table<ProcurementContactFromQuery>
        dataSource={data}
        columns={columns}
        rowKey={(record) => record.name}
        loading={isLoading}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No procurement contacts found"
            />
          ),
        }}
        isError={isError}
        pagination={{
          current: currentPage,
          pageSize,
          onChange: (pageNumber, pageSize) => {
            setPageSize(pageSize);
            setCurrentPage(pageNumber);
            logEvent(EventNames.paginationSelected, { Value: pageNumber });
          },
        }}
      />
    </div>
  );
}

/** The freshest value inline, with the remainder behind an "N more" popover. */
function ContactValuesCell({
  values,
  contactName,
  dataType,
  label,
  placement,
}: {
  values: ContactValue[];
  contactName: string;
  dataType: "email" | "phone";
  label: string;
  placement: "left" | "right";
}) {
  const [first, ...rest] = values;

  if (rest.length === 0) {
    return <ContactDetail data={first?.value} dataType={dataType} />;
  }

  return (
    <>
      <ContactDetail data={first.value} dataType={dataType} /> and{" "}
      <Popover
        trigger="click"
        overlayClassName={css.overflowPopover}
        title={
          <>
            {label} {contactName.length > 0 ? `for ${contactName}` : ""}
          </>
        }
        placement={placement}
        content={
          <List>
            {values.map((value) => (
              <List.Item key={value.value}>
                <ContactDetail data={value.value} dataType={dataType} />
              </List.Item>
            ))}
          </List>
        }
      >
        <TextButton>{rest.length} more</TextButton>
      </Popover>
    </>
  );
}

export function ContactDetail({
  data,
  dataType,
  requiredDataSubscription,
}: {
  data: string;
  dataType: "email" | "phone";
  requiredDataSubscription?: DataTypeName;
}): JSX.Element {
  const { message } = App.useApp();
  const { logEvent } = useTracking();
  const subscription = useSubscription();
  const canViewAdvancedContacts = requiredDataSubscription
    ? subscription.hasDataTypes(requiredDataSubscription)
    : true;

  const handleCopyToClipboard = async (contactData: string | null) => {
    if (contactData) {
      switch (dataType) {
        case "email":
          logEvent(EventNames.emailCopied, {
            Value: contactData,
          });
          break;
        case "phone":
          logEvent(EventNames.phoneNumberCopied, {
            Value: contactData,
          });
      }
      const content: ClipboardContent = { plain: contactData };
      const copied = await copyToClipboard(content);

      if (copied) {
        void message.success("Copied!", 2);
      }
    }
  };

  return canViewAdvancedContacts ? (
    <Tooltip side="top" content="Copy to clipboard">
      <span className={css.contactDetail} onClick={async () => handleCopyToClipboard(data)}>
        {data}
      </span>
    </Tooltip>
  ) : (
    <span className={css.contactDetail}>
      {dataType === "email" ? (
        <>
          <span className={css.blurred}>{data}</span>@<span className={css.blurred}>{data}</span>
          .gov.uk
        </>
      ) : (
        <>
          +44 <span className={css.blurred}>{data}</span>
        </>
      )}
    </span>
  );
}
